import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SpeedIcon from "@mui/icons-material/Speed";
import TimelineIcon from "@mui/icons-material/Timeline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { Gauge } from "@mui/x-charts/Gauge";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

type Proyecto = {
  id: number;
  codigo: string;
  nombre: string;
  solicitante: string | null;
  tipo_proyecto: string | null;
  municipio: string | null;
  responsable_emcali: string | null;
  fecha_radicado: string | null;
  estado_global: string | null;
  solicitud_decision: string | null;
  created_at: string;
  updated_at: string;
};

type ResumenReportes = {
  indicadores: {
    total_proyectos: number;
    en_revision: number;
    aprobados: number;
    rechazados: number;
  };
  etapas: Array<{
    etapa_key: string;
    etapa_nombre: string;
    total: number;
    completadas: number;
    pendientes: number;
  }>;
};

const ETAPAS_BASE: Array<{ etapa_key: string; etapa_nombre: string }> = [
  { etapa_key: "solicitud_servicio", etapa_nombre: "Solicitud del servicio" },
  { etapa_key: "factibilidad_servicio", etapa_nombre: "Diseño" },
  { etapa_key: "revision_aprobacion", etapa_nombre: "Supervisión y aprobación" },
  { etapa_key: "ejecucion_obras", etapa_nombre: "Ejecución de obras" },
  { etapa_key: "puesta_servicio", etapa_nombre: "Puesta en servicio" },
];

const UNIDAD_POR_ETAPA: Record<string, string> = {
  solicitud_servicio: "Prospectiva",
  factibilidad_servicio: "Proyectos",
  revision_aprobacion: "Grandes Clientes y Proyectos",
  ejecucion_obras: "Mantenimiento Operación y Proyectos",
  puesta_servicio: "Control de Energía",
};

const ETAPA_NOMBRE_CANONICO: Record<string, string> = ETAPAS_BASE.reduce(
  (acc, etapa) => ({ ...acc, [etapa.etapa_key]: etapa.etapa_nombre }),
  {} as Record<string, string>,
);

const resolverEtapaCanonica = (etapaKey?: string, etapaNombre?: string) => {
  const key = String(etapaKey || "").toLowerCase();
  const name = String(etapaNombre || "").toLowerCase();
  const text = `${key} ${name}`;

  if (text.includes("solicitud")) return "solicitud_servicio";
  if (text.includes("factibilidad") || text.includes("diseno") || text.includes("diseño")) {
    return "factibilidad_servicio";
  }
  if (text.includes("revision") || text.includes("aprobacion") || text.includes("aprobación")) {
    return "revision_aprobacion";
  }
  if (text.includes("ejecucion") || text.includes("ejecución") || text.includes("obra")) {
    return "ejecucion_obras";
  }
  if (text.includes("puesta") || text.includes("servicio")) return "puesta_servicio";
  return key;
};

const nombreEtapaCanonico = (etapaKey?: string, etapaNombre?: string) => {
  const canon = resolverEtapaCanonica(etapaKey, etapaNombre);
  return ETAPA_NOMBRE_CANONICO[canon] || etapaNombre || "-";
};

type TiemposProyecto = {
  proyecto: { id: number; codigo: string; nombre: string; created_at: string; updated_at: string };
  tiempos: Array<{
    etapa_key: string;
    etapa_nombre: string;
    estado: string;
    porcentaje: number;
    fecha_inicio: string | null;
    fecha_fin: string | null;
    dias_en_etapa: number;
  }>;
};

const getAuthToken = () => {
  const encodedToken = localStorage.getItem("authToken");
  return encodedToken ? atob(encodedToken) : "";
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-CO");
};

const formatMes = (ym: string) => {
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("es-CO", { month: "short", year: "2-digit" });
};

const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);

const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

const normalizarEtapas = (etapas?: ResumenReportes["etapas"]) => {
  const porKey = new Map<string, { etapa_nombre: string; total: number; completadas: number; pendientes: number }>();
  (etapas || []).forEach((e) => {
    const canon = resolverEtapaCanonica(e.etapa_key, e.etapa_nombre);
    const prev = porKey.get(canon);
    if (prev) {
      porKey.set(canon, {
        etapa_nombre: prev.etapa_nombre,
        total: prev.total + Number(e.total || 0),
        completadas: prev.completadas + Number(e.completadas || 0),
        pendientes: prev.pendientes + Number(e.pendientes || 0),
      });
      return;
    }
    porKey.set(canon, {
      etapa_nombre: e.etapa_nombre,
      total: Number(e.total || 0),
      completadas: Number(e.completadas || 0),
      pendientes: Number(e.pendientes || 0),
    });
  });
  return ETAPAS_BASE.map((base) => {
    const actual = porKey.get(base.etapa_key);
    return {
      etapa_key: base.etapa_key,
      etapa_nombre: actual?.etapa_nombre || base.etapa_nombre,
      total: actual?.total ?? 0,
      completadas: actual?.completadas ?? 0,
      pendientes: actual?.pendientes ?? 0,
    };
  });
};

const COL = {
  brand: "#1b5e20",
  brandLight: "#2e7d32",
  teal: "#0d9488",
  blue: "#2563eb",
  amber: "#d97706",
  rose: "#e11d48",
  slate: "#64748b",
  cardBg: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
};

const CHART_AXIS = {
  tickLabelStyle: { fontSize: 12 },
};

/** La leyenda por defecto de MUI X Charts se superpone al área del gráfico; la ocultamos y usamos HTML debajo. */
const CHART_HIDE_LEGEND = {
  legend: { hidden: true },
} as const;

function ChartLegend({ items }: { items: Array<{ label: string; color: string }> }) {
  if (!items.length) return null;
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ mt: 1.5, pt: 1, borderTop: "1px solid", borderColor: "divider" }}
    >
      {items.map((item) => (
        <Stack key={item.label} direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.75,
              bgcolor: item.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            {item.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function ChartPlot({ children, legend }: { children: React.ReactNode; legend?: React.ReactNode }) {
  return (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>{children}</Box>
      {legend}
    </Stack>
  );
}

function SectionHeader({
  kicker,
  title,
  subtitle,
  icon,
}: {
  kicker?: string;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
      {icon && (
        <Box
          sx={{
            mt: 0.25,
            width: 40,
            height: 40,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(COL.brand, 0.1),
            color: COL.brand,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      )}
      <Box>
        {kicker && (
          <Typography variant="overline" color="primary" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {kicker}
          </Typography>
        )}
        <Typography variant="h6" fontWeight={800}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, maxWidth: 720 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function ChartPanel({
  title,
  subtitle,
  action,
  children,
  minHeight = 360,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  minHeight?: number;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5, gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      <Box sx={{ flex: 1, minHeight, width: "100%" }}>{children}</Box>
    </Paper>
  );
}

function KpiCard(props: {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  progress: number;
}) {
  const { title, value, subtitle, icon, color, progress } = props;
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background: COL.cardBg,
        overflow: "visible",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          borderRadius: "12px 12px 0 0",
          background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.5)})`,
        }}
      />
      <CardContent sx={{ pt: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.4 }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ color: "text.primary", lineHeight: 1.1 }}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(color, 0.12),
              color,
            }}
          >
            {icon}
          </Box>
        </Stack>
        <Stack spacing={0.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Participación
            </Typography>
            <Typography variant="caption" fontWeight={700}>
              {progress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, progress)}
            sx={{
              height: 8,
              borderRadius: 99,
              bgcolor: alpha(color, 0.12),
              "& .MuiLinearProgress-bar": {
                borderRadius: 99,
                background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.75)})`,
              },
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function decisionChip(decision: string | null) {
  const d = String(decision || "-").toLowerCase();
  if (d.includes("aprob")) return <Chip size="small" label={decision || "-"} color="success" variant="outlined" />;
  if (d.includes("rech") || d.includes("desaprob")) return <Chip size="small" label={decision || "-"} color="error" variant="outlined" />;
  if (d.includes("revision") || d.includes("revisión")) return <Chip size="small" label={decision || "-"} color="warning" variant="outlined" />;
  return <Chip size="small" label={decision || "-"} variant="outlined" />;
}

function EmptyChart({ message }: { message: string }) {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", minHeight: 200, py: 3 }}>
      <Typography color="text.secondary" textAlign="center">
        {message}
      </Typography>
    </Stack>
  );
}

export default function ContenidoPrincipal() {
  const theme = useTheme();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [proyectos, setProyectos] = React.useState<Proyecto[]>([]);
  const [resumen, setResumen] = React.useState<ResumenReportes | null>(null);
  const [codigoBusqueda, setCodigoBusqueda] = React.useState("");
  const [tiemposProyecto, setTiemposProyecto] = React.useState<TiemposProyecto | null>(null);
  const [loadingBusqueda, setLoadingBusqueda] = React.useState(false);

  const baseUrl = getProyectosApiBaseUrl();
  const token = React.useMemo(() => getAuthToken(), []);

  React.useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const [resProyectos, resResumen] = await Promise.allSettled([
          fetch(`${baseUrl}`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
          fetch(`${baseUrl}/reportes/resumen`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
        ]);

        let cargado = false;

        if (resProyectos.status === "fulfilled" && resProyectos.value.ok) {
          setProyectos(await resProyectos.value.json());
          cargado = true;
        } else {
          setProyectos([]);
        }

        if (resResumen.status === "fulfilled" && resResumen.value.ok) {
          const dataResumen: ResumenReportes = await resResumen.value.json();
          setResumen({
            indicadores: dataResumen.indicadores,
            etapas: normalizarEtapas(dataResumen.etapas),
          });
          cargado = true;
        } else {
          setResumen({
            indicadores: { total_proyectos: 0, en_revision: 0, aprobados: 0, rechazados: 0 },
            etapas: normalizarEtapas([]),
          });
        }

        if (!cargado) throw new Error("No fue posible cargar información del dashboard.");
      } catch (err: any) {
        setError(err.message || "Error al consultar reportes.");
      } finally {
        setLoading(false);
      }
    };

    if (!token) {
      setError("No hay sesión activa. Inicia sesión nuevamente.");
      setLoading(false);
      return;
    }

    fetchDashboard();
  }, [baseUrl, token]);

  const buscarTiempos = async () => {
    if (!codigoBusqueda.trim()) return;
    try {
      setLoadingBusqueda(true);
      setError(null);
      const response = await fetch(
        `${baseUrl}/reportes/tiempos?codigo=${encodeURIComponent(codigoBusqueda.trim())}`,
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "No fue posible consultar tiempos del proyecto.");
      setTiemposProyecto(data);
    } catch (err: any) {
      setError(err.message || "Error al consultar tiempos.");
      setTiemposProyecto(null);
    } finally {
      setLoadingBusqueda(false);
    }
  };

  const total = resumen?.indicadores.total_proyectos ?? 0;
  const ind = resumen?.indicadores;

  const etapasPct = React.useMemo(() => {
    return (resumen?.etapas || []).map((e) => ({
      ...e,
      pctCompletado: pct(e.completadas, e.total),
      pctPendiente: pct(e.pendientes, e.total),
    }));
  }, [resumen?.etapas]);

  const saludPipeline = React.useMemo(() => {
    if (!etapasPct.length) return 0;
    const sum = etapasPct.reduce((acc, e) => acc + e.pctCompletado, 0);
    return Math.round((sum / etapasPct.length) * 10) / 10;
  }, [etapasPct]);

  const radicacionesSerie = React.useMemo(() => {
    const map = new Map<string, number>();
    proyectos.forEach((p) => {
      const raw = p.created_at || p.fecha_radicado;
      if (!raw) return;
      const key = new Date(raw).toISOString().slice(0, 7);
      map.set(key, (map.get(key) || 0) + 1);
    });
    const keys = Array.from(map.keys()).sort();
    return keys.slice(-8).map((k) => ({ mes: k, label: formatMes(k), count: map.get(k) || 0 }));
  }, [proyectos]);

  const municipiosTop = React.useMemo(() => {
    const map = new Map<string, number>();
    proyectos.forEach((p) => {
      const m = String(p.municipio || "Sin municipio").trim() || "Sin municipio";
      map.set(m, (map.get(m) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [proyectos]);

  const tiposProyecto = React.useMemo(() => {
    const map = new Map<string, number>();
    proyectos.forEach((p) => {
      const t = String(p.tipo_proyecto || "Sin tipo").trim() || "Sin tipo";
      map.set(t, (map.get(t) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [proyectos]);

  const pieSlices = React.useMemo(() => {
    const rev = ind?.en_revision ?? 0;
    const apr = ind?.aprobados ?? 0;
    const rec = ind?.rechazados ?? 0;
    return [
      { id: 0, value: rev, label: "En revisión", color: COL.amber },
      { id: 1, value: apr, label: "Aprobados", color: COL.brandLight },
      { id: 2, value: rec, label: "Rechazados", color: COL.rose },
    ].filter((s) => s.value > 0);
  }, [ind]);

  const etapaLabels = etapasPct.map((e) => truncate(e.etapa_nombre, 24));
  const municipioLabels = municipiosTop.map(([m]) => truncate(m, 20));
  const tipoLabels = tiposProyecto.map(([t]) => truncate(t, 16));

  return (
    <Box
      sx={{
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        pb: 5,
        minHeight: "100%",
        background: `linear-gradient(180deg, ${alpha(COL.brand, 0.06)} 0%, ${theme.palette.background.default} 280px)`,
      }}
    >
      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 4,
          color: "#fff",
          background: `linear-gradient(125deg, ${COL.brand} 0%, #0d3d12 45%, ${COL.brandLight} 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: -40,
            top: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            bgcolor: alpha("#fff", 0.08),
          }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ position: "relative" }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: alpha("#fff", 0.15),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AssessmentIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
              Dashboard · Proyectos EMCALI
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 760 }}>
              Resumen del portafolio, avance por etapa del pipeline y distribución geográfica.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={22} sx={{ color: COL.brand }} />
          <Typography fontWeight={600}>Sincronizando datos…</Typography>
        </Stack>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} xl={3}>
          <KpiCard title="Total proyectos" value={total} subtitle="Activos en el módulo" icon={<SpeedIcon />} color={COL.slate} progress={100} />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <KpiCard title="En revisión" value={ind?.en_revision ?? 0} subtitle="Sin decisión final" icon={<HourglassEmptyIcon />} color={COL.amber} progress={pct(ind?.en_revision ?? 0, total)} />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <KpiCard title="Aprobados" value={ind?.aprobados ?? 0} subtitle="Solicitud aprobada" icon={<CheckCircleOutlineIcon />} color={COL.brandLight} progress={pct(ind?.aprobados ?? 0, total)} />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <KpiCard title="Rechazados" value={ind?.rechazados ?? 0} subtitle="Rechazada / desaprobada" icon={<WarningAmberIcon />} color={COL.rose} progress={pct(ind?.rechazados ?? 0, total)} />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Sección 1: Pipeline */}
      <SectionHeader
        kicker="Avance operativo"
        title="Pipeline de 5 etapas"
        subtitle="Completado vs pendiente por etapa, decisiones de solicitud y salud general del flujo."
        icon={<TimelineIcon />}
      />
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={7}>
          <ChartPanel
            title="Completado vs pendiente (%)"
            subtitle="Barras apiladas al 100 % por etapa del flujo CREG."
            action={<Chip icon={<DonutLargeIcon />} label="Proporcional" size="small" variant="outlined" />}
            minHeight={340}
          >
            <ChartPlot
              legend={
                <ChartLegend
                  items={[
                    { label: "Completado", color: COL.teal },
                    { label: "Pendiente", color: alpha(COL.blue, 0.75) },
                  ]}
                />
              }
            >
              <BarChart
                layout="horizontal"
                height={300}
                grid={{ vertical: true, horizontal: true }}
                margin={{ left: 148, right: 24, top: 16, bottom: 24 }}
                slotProps={CHART_HIDE_LEGEND}
                yAxis={[{ scaleType: "band", data: etapaLabels, tickLabelStyle: CHART_AXIS.tickLabelStyle }]}
                xAxis={[{ min: 0, max: 100, label: "% en la etapa" }]}
                series={[
                  { data: etapasPct.map((e) => e.pctCompletado), label: "Completado", stack: "tot", color: COL.teal },
                  { data: etapasPct.map((e) => e.pctPendiente), label: "Pendiente", stack: "tot", color: alpha(COL.blue, 0.75) },
                ]}
              />
            </ChartPlot>
          </ChartPanel>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            <ChartPanel title="Decisiones de solicitud" subtitle="Distribución del portafolio." minHeight={220}>
              {pieSlices.length === 0 ? (
                <EmptyChart message="Sin datos para graficar." />
              ) : (
                <ChartPlot
                  legend={
                    <ChartLegend items={pieSlices.map((s) => ({ label: s.label, color: s.color }))} />
                  }
                >
                  <PieChart
                    height={200}
                    margin={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    slotProps={CHART_HIDE_LEGEND}
                    series={[
                      {
                        innerRadius: 44,
                        outerRadius: 80,
                        paddingAngle: 2,
                        cornerRadius: 4,
                        data: pieSlices,
                        arcLabelMinAngle: 14,
                      },
                    ]}
                  />
                </ChartPlot>
              )}
            </ChartPanel>

            <ChartPanel title="Salud del pipeline" subtitle="Promedio de etapas completadas." minHeight={200}>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <Gauge
                  width={180}
                  height={180}
                  value={saludPipeline}
                  valueMin={0}
                  valueMax={100}
                  startAngle={-110}
                  endAngle={110}
                  sx={{
                    [`& .MuiGauge-valueText`]: { fontSize: 22, fontWeight: 800 },
                    [`& .MuiGauge-valueArc`]: { fill: COL.brandLight },
                  }}
                  text={({ value }) => `${value ?? 0}%`}
                />
              </Box>
            </ChartPanel>
          </Stack>
        </Grid>
      </Grid>

      {/* Sección 2: Tendencias */}
      <SectionHeader
        kicker="Evolución"
        title="Tendencias del portafolio"
        subtitle="Curva de avance por etapa y radicaciones mensuales."
        icon={<ShowChartIcon />}
      />
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <ChartPanel title="Curva de avance por etapa" subtitle="% de filas completadas en cada paso." minHeight={300}>
            <ChartPlot legend={<ChartLegend items={[{ label: "% completado", color: COL.brandLight }]} />}>
              <LineChart
                height={260}
                grid={{ vertical: true, horizontal: true }}
                margin={{ left: 48, right: 20, top: 16, bottom: 40 }}
                slotProps={CHART_HIDE_LEGEND}
                xAxis={[
                  {
                    scaleType: "point",
                    data: etapasPct.map((_, i) => `Paso ${i + 1}`),
                    tickLabelStyle: CHART_AXIS.tickLabelStyle,
                  },
                ]}
                yAxis={[{ min: 0, max: 100, label: "%" }]}
                series={[
                  {
                    data: etapasPct.map((e) => e.pctCompletado),
                    label: "% completado",
                    color: COL.brandLight,
                    area: true,
                    showMark: true,
                    curve: "natural",
                  },
                ]}
              />
            </ChartPlot>
          </ChartPanel>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartPanel
            title="Radicaciones por mes"
            subtitle="Últimos períodos con actividad de creación de proyectos."
            action={<TrendingUpIcon color="action" fontSize="small" />}
            minHeight={300}
          >
            {radicacionesSerie.length === 0 ? (
              <EmptyChart message="Aún no hay radicaciones registradas." />
            ) : (
              <ChartPlot legend={<ChartLegend items={[{ label: "Radicaciones", color: COL.teal }]} />}>
                <LineChart
                  height={260}
                  grid={{ vertical: true, horizontal: true }}
                  margin={{ left: 48, right: 20, top: 16, bottom: 52 }}
                  slotProps={CHART_HIDE_LEGEND}
                  xAxis={[
                    {
                      scaleType: "point",
                      data: radicacionesSerie.map((r) => r.label),
                      tickLabelStyle: { ...CHART_AXIS.tickLabelStyle, angle: -20, textAnchor: "end" },
                    },
                  ]}
                  yAxis={[{ min: 0, label: "Proyectos" }]}
                  series={[
                    {
                      data: radicacionesSerie.map((r) => r.count),
                      label: "Radicaciones",
                      color: COL.teal,
                      area: true,
                      showMark: true,
                      curve: "natural",
                    },
                  ]}
                />
              </ChartPlot>
            )}
          </ChartPanel>
        </Grid>
      </Grid>

      {/* Sección 3: Distribución */}
      <SectionHeader
        kicker="Distribución"
        title="Geografía y tipología"
        subtitle="Concentración de proyectos por municipio y tipo."
        icon={<LocationCityIcon />}
      />
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={6}>
          <ChartPanel title="Municipios con más proyectos" minHeight={320}>
            {municipiosTop.length === 0 ? (
              <EmptyChart message="Sin datos de municipio." />
            ) : (
              <ChartPlot legend={<ChartLegend items={[{ label: "Número de proyectos", color: alpha(COL.blue, 0.85) }]} />}>
                <BarChart
                  layout="horizontal"
                  height={280}
                  grid={{ vertical: true, horizontal: true }}
                  margin={{ left: 128, right: 24, top: 16, bottom: 32 }}
                  slotProps={CHART_HIDE_LEGEND}
                  yAxis={[{ scaleType: "band", data: municipioLabels, tickLabelStyle: CHART_AXIS.tickLabelStyle }]}
                  xAxis={[{ label: "Proyectos" }]}
                  series={[{ data: municipiosTop.map(([, c]) => c), label: "Número de proyectos", color: alpha(COL.blue, 0.85) }]}
                />
              </ChartPlot>
            )}
          </ChartPanel>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartPanel title="Proyectos por tipo" minHeight={320}>
            {tiposProyecto.length === 0 ? (
              <EmptyChart message="Sin datos de tipo de proyecto." />
            ) : (
              <ChartPlot legend={<ChartLegend items={[{ label: "Proyectos", color: alpha(COL.brandLight, 0.9) }]} />}>
                <BarChart
                  height={280}
                  grid={{ vertical: true, horizontal: true }}
                  margin={{ bottom: 56, left: 48, right: 20, top: 16 }}
                  slotProps={CHART_HIDE_LEGEND}
                  xAxis={[
                    {
                      scaleType: "band",
                      data: tipoLabels,
                      tickLabelStyle: { angle: -30, textAnchor: "end", fontSize: 11 },
                    },
                  ]}
                  yAxis={[{ label: "Proyectos" }]}
                  series={[{ data: tiposProyecto.map(([, c]) => c), label: "Proyectos", color: alpha(COL.brandLight, 0.9) }]}
                />
              </ChartPlot>
            )}
          </ChartPanel>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Sección 4: Tabla flujo */}
      <SectionHeader
        kicker="Detalle"
        title="Flujo y unidad responsable"
        subtitle="Avance numérico por paso del pipeline y área EMCALI asignada."
      />
      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, mb: 4, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(COL.brand, 0.08) }}>
                <TableCell width={72}><strong>Paso</strong></TableCell>
                <TableCell><strong>Etapa</strong></TableCell>
                <TableCell><strong>Unidad</strong></TableCell>
                <TableCell width={120} align="right"><strong>Completadas</strong></TableCell>
                <TableCell width={120} align="right"><strong>Total</strong></TableCell>
                <TableCell sx={{ minWidth: 220 }}><strong>Avance</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ETAPAS_BASE.map((etapa, idx) => {
                const row = etapasPct.find((e) => e.etapa_key === etapa.etapa_key);
                const t = row?.total ?? 0;
                const c = row?.completadas ?? 0;
                const bar = t > 0 ? (c / t) * 100 : 0;
                return (
                  <TableRow key={etapa.etapa_key} hover>
                    <TableCell>
                      <Chip label={idx + 1} size="small" sx={{ fontWeight: 800, bgcolor: alpha(COL.brand, 0.12) }} />
                    </TableCell>
                    <TableCell>{etapa.etapa_nombre}</TableCell>
                    <TableCell>{UNIDAD_POR_ETAPA[etapa.etapa_key] || "-"}</TableCell>
                    <TableCell align="right">{c}</TableCell>
                    <TableCell align="right">{t}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LinearProgress
                          variant="determinate"
                          value={bar}
                          sx={{
                            flex: 1,
                            height: 10,
                            borderRadius: 99,
                            bgcolor: alpha(COL.teal, 0.15),
                            "& .MuiLinearProgress-bar": { borderRadius: 99, bgcolor: COL.teal },
                          }}
                        />
                        <Typography variant="caption" fontWeight={700} sx={{ width: 44, textAlign: "right" }}>
                          {Math.round(bar)}%
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Sección 5: Herramientas y listado */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={5}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, border: "1px solid", borderColor: "divider", height: "100%" }}>
            <SectionHeader title="Consultar tiempos por proyecto" subtitle="Ingrese el código para ver días en cada etapa." />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
              <TextField
                label="Código (ej: UPM-12)"
                value={codigoBusqueda}
                onChange={(e) => setCodigoBusqueda(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buscarTiempos()}
                fullWidth
                size="small"
              />
              <Button variant="contained" onClick={buscarTiempos} disabled={loadingBusqueda} sx={{ minWidth: 140, flexShrink: 0 }}>
                {loadingBusqueda ? "Buscando…" : "Consultar"}
              </Button>
            </Stack>
            {tiemposProyecto?.proyecto && (
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <strong>{tiemposProyecto.proyecto.codigo}</strong> — {tiemposProyecto.proyecto.nombre}
              </Typography>
            )}
            {tiemposProyecto?.tiempos?.length ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(COL.brand, 0.06) }}>
                      <TableCell>Etapa</TableCell>
                      <TableCell align="right">Días</TableCell>
                      <TableCell width={100} align="right">Avance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tiemposProyecto.tiempos.map((t) => (
                      <TableRow key={t.etapa_key}>
                        <TableCell>{nombreEtapaCanonico(t.etapa_key, t.etapa_nombre)}</TableCell>
                        <TableCell align="right">{t.dias_en_etapa}</TableCell>
                        <TableCell align="right">{t.porcentaje}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Use el buscador para ver tiempos de un proyecto específico.
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
            <SectionHeader title="Proyectos recientes" subtitle="Últimos 15 registros del módulo." />
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader sx={{ "& tbody tr:nth-of-type(even)": { bgcolor: alpha(COL.slate, 0.04) } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: alpha(COL.brand, 0.08), fontWeight: 700 }}>Código</TableCell>
                    <TableCell sx={{ bgcolor: alpha(COL.brand, 0.08), fontWeight: 700 }}>Nombre</TableCell>
                    <TableCell sx={{ bgcolor: alpha(COL.brand, 0.08), fontWeight: 700 }}>Municipio</TableCell>
                    <TableCell sx={{ bgcolor: alpha(COL.brand, 0.08), fontWeight: 700 }}>Estado</TableCell>
                    <TableCell sx={{ bgcolor: alpha(COL.brand, 0.08), fontWeight: 700 }}>Decisión</TableCell>
                    <TableCell sx={{ bgcolor: alpha(COL.brand, 0.08), fontWeight: 700 }}>Radicado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proyectos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No hay proyectos registrados.
                      </TableCell>
                    </TableRow>
                  ) : (
                    proyectos.slice(0, 15).map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{p.codigo || "-"}</TableCell>
                        <TableCell>{truncate(p.nombre || "-", 40)}</TableCell>
                        <TableCell>{p.municipio || "-"}</TableCell>
                        <TableCell>
                          <Chip size="small" label={truncate(p.estado_global || "-", 28)} variant="outlined" />
                        </TableCell>
                        <TableCell>{decisionChip(p.solicitud_decision)}</TableCell>
                        <TableCell>{formatDate(p.fecha_radicado)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
